import type { Drawing } from '@manudev/model';
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useDrawingsStore } from './state';

interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MARGIN = 0.06;
const MAX_ZOOM = 24;

// The export as it comes, minus its white sheet, inside our own <svg> so the page's styles
// reach every line; room labels on top, placed in sheet millimetres. Wheel zooms, drag pans.
export function Sheet({ drawing }: { drawing: Drawing | null }) {
  const svg = useRef<SVGSVGElement>(null);
  const art = useRef<SVGGElement>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [size, setSize] = useState<[number, number]>([1, 1]);
  const hovered = useDrawingsStore((s) => s.hoveredRoom);
  const hoverRoom = useDrawingsStore((s) => s.hoverRoom);
  const fit = useMemo(() => (drawing ? fitOf(drawing.bounds) : null), [drawing]);
  const shown = view ?? fit;
  // sheet millimetres per screen pixel, so labels keep their size at any zoom
  const k = shown ? Math.max(shown.w / size[0], shown.h / size[1]) : 1;

  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect;
      if (box) setSize([box.width || 1, box.height || 1]);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const host = art.current;
    if (!host) return;
    host.replaceChildren(...(drawing ? artOf(drawing.svg) : []));
  }, [drawing]);

  useEffect(() => {
    const el = svg.current;
    if (!el || !fit) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const p = toSheet(el, e.clientX, e.clientY);
      if (!p) return;
      const k = Math.exp(e.deltaY * 0.0015);
      setView((v) => {
        const cur = v ?? fit;
        const w = Math.min(fit.w * 2, Math.max(fit.w / MAX_ZOOM, cur.w * k));
        const s = w / cur.w;
        return { x: p.x - (p.x - cur.x) * s, y: p.y - (p.y - cur.y) * s, w, h: cur.h * s };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [fit]);

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const from = drag.current;
    const ctm = svg.current?.getScreenCTM();
    if (!from || !ctm || !shown) return;
    const dx = (e.clientX - from.x) / ctm.a;
    const dy = (e.clientY - from.y) / ctm.d;
    drag.current = { x: e.clientX, y: e.clientY };
    setView((v) => {
      const cur = v ?? shown;
      return { ...cur, x: cur.x - dx, y: cur.y - dy };
    });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <div className="sheet">
      <svg
        ref={svg}
        viewBox={shown ? `${shown.x} ${shown.y} ${shown.w} ${shown.h}` : '0 0 1 1'}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => setView(null)}
        role="img"
        aria-label="Drawing"
      >
        <g ref={art} className="sheet-art" />
        <g className="sheet-rooms">
          {drawing?.rooms.map((r) => {
            const w = Math.max(r.name.length, 8) * 6.2 + 10;
            return (
              <g
                key={r.globalId}
                className="room"
                data-hover={hovered === r.globalId || undefined}
                transform={`translate(${r.label[0]} ${r.label[1]}) scale(${k})`}
                onPointerEnter={() => hoverRoom(r.globalId)}
                onPointerLeave={() => hoverRoom(null)}
              >
                <rect x={-w / 2} y={-14} width={w} height={27} rx={4} />
                <text y={-2}>{r.name}</text>
                <text y={9} className="room-area">
                  {r.area.toFixed(2)} m²
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function fitOf({ min, max }: Drawing['bounds']): View {
  const w = max[0] - min[0];
  const h = max[1] - min[1];
  const m = Math.max(w, h) * MARGIN;
  return { x: min[0] - m, y: min[1] - m, w: w + 2 * m, h: h + 2 * m };
}

// The export's own children (hatch patterns and the line layers) without the white sheet.
function artOf(svg: string): Node[] {
  const root = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
  if (root.nodeName !== 'svg') return [];
  return [...root.children]
    .filter((n) => n.nodeName !== 'rect')
    .map((n) => document.importNode(n, true));
}

function toSheet(el: SVGSVGElement, x: number, y: number): DOMPoint | null {
  const ctm = el.getScreenCTM();
  return ctm ? new DOMPoint(x, y).matrixTransform(ctm.inverse()) : null;
}
